import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  AlertDescription,
  Button,
  DatePicker,
  datePickerLocaleFor,
  DialogShellBody,
  DialogShellFooter,
  DialogShellHeader,
  Field,
  FieldContent,
  FieldError,
  FieldLabel,
  FormDialogShell,
  Input,
  Label,
  parseIsoDate
} from '@bliks-io/ui';
import {
  ComplianceCheckAppConfigSchema,
  isContractAnchorInFuture,
  NORMIFY_NORM_CATALOG,
  type ComplianceCheckAppConfig
} from '@bliks-io/contracts';

import { FormFieldMultiSelect } from '@/components/form/FormFieldMultiSelect';
import { formatDate } from '@/lib/formatters';
import { useSetTenantAppConfig } from '@/pages/admin/api/queries/useTenantApps';
import type { MergedAppRow } from '@/lib/merge-app-catalog';

/** The form's own shape: every field is text until the schema accepts it. */
interface ConfigDraft {
  norms: { normifyIdentifier: string; label: string }[];
  requestAllowance: string;
  contractAnchor: string;
}

const EMPTY_DRAFT: ConfigDraft = { norms: [], requestAllowance: '', contractAnchor: '' };

/**
 * The stored config as the form sees it. `unreadable` marks a value the current
 * schema cannot read — the shape drifted after it was written. The form cannot
 * show it, and saving replaces it, so the operator has to be told it exists: an
 * empty form is otherwise indistinguishable from an unconfigured tenant.
 *
 * A repeated identifier collapses here, at the boundary: the picker can only
 * show one selection per identifier, so a draft that kept both would save a
 * duplicate the operator was never shown and never asked for.
 */
function readStored(stored: unknown): { draft: ConfigDraft; unreadable: boolean } {
  const parsed = ComplianceCheckAppConfigSchema.safeParse(stored);
  if (!parsed.success) {
    return { draft: EMPTY_DRAFT, unreadable: stored !== undefined };
  }
  const firstNormPerIdentifier = parsed.data.norms.filter(
    (norm, index) =>
      parsed.data.norms.findIndex(
        (candidate) => candidate.normifyIdentifier === norm.normifyIdentifier
      ) === index
  );
  return {
    draft: {
      norms: firstNormPerIdentifier,
      requestAllowance: String(parsed.data.requestAllowance),
      contractAnchor: parsed.data.contractAnchor
    },
    unreadable: false
  };
}

/**
 * The catalog widened by what this tenant already had stored: an identifier the
 * catalog does not list stays selectable, and a stored label beats the
 * catalog's, so a name customized for the tenant survives a deselect.
 *
 * Built from the STORED norms, not the live draft — a norm taken out of the
 * selection has to remain in the list to be put back.
 */
function normLabelsFor(storedNorms: ConfigDraft['norms']): Map<string, string> {
  const labelByIdentifier = new Map<string, string>();
  for (const norm of NORMIFY_NORM_CATALOG) {
    labelByIdentifier.set(norm.normifyIdentifier, norm.label);
  }
  for (const norm of storedNorms) {
    labelByIdentifier.set(norm.normifyIdentifier, norm.label);
  }
  return labelByIdentifier;
}

/**
 * The allowance stays text right up to the parse: an unparseable entry has to
 * reach the schema as the string it is and be refused, because `Number('abc')`
 * would otherwise turn a typo into a silent allowance of zero.
 */
function parseDraft(draft: ConfigDraft) {
  const requestAllowance = draft.requestAllowance.trim();
  return ComplianceCheckAppConfigSchema.safeParse({
    norms: draft.norms,
    requestAllowance: /^\d+$/.test(requestAllowance) ? Number(requestAllowance) : requestAllowance,
    contractAnchor: draft.contractAnchor
  });
}

interface ComplianceCheckConfigDialogProps {
  stored: unknown;
  isSaving: boolean;
  onClose: () => void;
  onSave: (config: ComplianceCheckAppConfig) => void;
}

/**
 * Mounted only while open, so the draft re-seeds from the stored config on every
 * open, and `FormDialogShell` prompts before a dirty backdrop/escape/cancel
 * discards edits — the repo's canonical unsaved-changes flow.
 */
function ComplianceCheckConfigDialog({
  stored,
  isSaving,
  onClose,
  onSave
}: ComplianceCheckConfigDialogProps) {
  const { t, i18n } = useTranslation();
  // Frozen at mount: the save writes the draft into the cached row before the
  // server has it, and a baseline that followed the row would read the
  // unsaved edits as clean.
  const [{ draft: initialDraft, unreadable }] = useState(() => readStored(stored));
  const [draft, setDraft] = useState<ConfigDraft>(initialDraft);
  const popupHost = useRef<HTMLDivElement | null>(null);
  const parsed = parseDraft(draft);
  const isDirty = JSON.stringify(draft) !== JSON.stringify(initialDraft);
  const anchorInFuture =
    draft.contractAnchor !== '' && isContractAnchorInFuture(draft.contractAnchor);

  const { normLabels, normItems } = useMemo(() => {
    const normLabels = normLabelsFor(initialDraft.norms);
    return {
      normLabels,
      normItems: [...normLabels].map(([value, label]) => ({ value, label }))
    };
  }, [initialDraft]);

  /**
   * Position follows the identifier order the multi-select emits — it appends a
   * newly picked norm and keeps the rest as they were — and the label comes from
   * `normLabels`, which is seeded stored-wins, so a name customized for the
   * tenant survives here just as it does across a deselect.
   */
  const selectNorms = (identifiers: string[]) =>
    setDraft((current) => ({
      ...current,
      norms: identifiers.map((identifier) => ({
        normifyIdentifier: identifier,
        label: normLabels.get(identifier) ?? identifier
      }))
    }));

  return (
    <FormDialogShell
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      isDirty={isDirty}
      isPending={isSaving}
      size="md"
      // The shell clips its children to its rounded corners; the calendar has to
      // hang past them.
      shellClassName="overflow-visible"
    >
      {({ close }) => (
        // The calendar portals in here rather than into the document: a modal
        // dialog decides what its focus trap keeps and what an outside press
        // dismisses by DOM containment. The host sits outside the scrolling
        // body on purpose — the body is the dialog's other clip box, and a
        // popup portalled into it is cut off at the first overflowing edge.
        // It stays a plain flex column, so an absolutely positioned popup is
        // no flex item and adds no gap.
        <div ref={popupHost} className="flex min-h-0 flex-1 flex-col">
          <DialogShellHeader title={t('adminSettings.tenant.appConfigTitle')} onClose={close} />
          <DialogShellBody>
            {unreadable && (
              <Alert variant="destructive">
                <AlertDescription>
                  {t('adminSettings.tenant.appConfigUnreadableDialog')}
                </AlertDescription>
              </Alert>
            )}
            <FormFieldMultiSelect
              id="app-config-norms"
              label={t('adminSettings.tenant.appConfigNorms')}
              placeholder={t('adminSettings.tenant.appConfigNormsPlaceholder')}
              items={normItems}
              value={draft.norms.map((norm) => norm.normifyIdentifier)}
              onChange={selectNorms}
              disabled={isSaving}
              showSelectedTags
            />

            <div className="space-y-2">
              <Label htmlFor="app-config-request-allowance">
                {t('adminSettings.tenant.appConfigRequestAllowance')}
              </Label>
              <Input
                id="app-config-request-allowance"
                type="text"
                inputMode="numeric"
                value={draft.requestAllowance}
                disabled={isSaving}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, requestAllowance: event.target.value }))
                }
              />
            </div>

            <Field data-invalid={anchorInFuture}>
              <FieldLabel
                id="app-config-contract-anchor-label"
                htmlFor="app-config-contract-anchor"
              >
                {t('adminSettings.tenant.appConfigContractAnchor')}
              </FieldLabel>
              <FieldContent>
                <DatePicker
                  value={draft.contractAnchor || null}
                  onValueCommit={(contractAnchor) =>
                    setDraft((current) => ({ ...current, contractAnchor }))
                  }
                  // The window has to outlive any real contract: an anchor ages
                  // one year every year, and once it falls outside the calendar
                  // drops its dropdowns and the operator cannot navigate back
                  // to it — the field has no clear affordance to escape with.
                  yearsInPast={30}
                  locale={datePickerLocaleFor(i18n.resolvedLanguage)}
                  popupLabel={t('adminSettings.tenant.appConfigContractAnchorPopup')}
                  portalContainer={popupHost}
                  disabled={isSaving}
                  trigger={
                    <Button
                      id="app-config-contract-anchor"
                      variant="outline"
                      // A button names itself from its content — the date — so
                      // the field label has to be named alongside it, or the
                      // purpose is never announced.
                      aria-labelledby="app-config-contract-anchor-label app-config-contract-anchor"
                      aria-invalid={anchorInFuture || undefined}
                      aria-describedby={
                        anchorInFuture ? 'app-config-contract-anchor-future' : undefined
                      }
                    >
                      {/* The ISO string is what the schema stores; the operator
                          reads the date in their own locale. Parsed to a local
                          day first — `new Date('2026-08-11')` is UTC midnight,
                          which prints as the day before west of Greenwich. */}
                      {draft.contractAnchor
                        ? formatDate(parseIsoDate(draft.contractAnchor), i18n.resolvedLanguage)
                        : t('adminSettings.tenant.appConfigContractAnchorPick')}
                    </Button>
                  }
                />
                {anchorInFuture && (
                  <FieldError id="app-config-contract-anchor-future">
                    {t('adminSettings.tenant.appConfigContractAnchorFuture')}
                  </FieldError>
                )}
              </FieldContent>
            </Field>
          </DialogShellBody>
          <DialogShellFooter
            secondary={{ label: t('common.cancel'), onClick: close, disabled: isSaving }}
            primary={{
              label: t('common.save'),
              onClick: () => {
                if (parsed.success) onSave(parsed.data);
              },
              disabled: !parsed.success || isSaving,
              loading: isSaving
            }}
          />
        </div>
      )}
    </FormDialogShell>
  );
}

interface TenantComplianceCheckConfigCellProps {
  tenantId: string;
  app: MergedAppRow;
}

/**
 * The per-tenant config editor for the BLIKS IO ISO Check app. Typed to that
 * app's config schema rather than generated from it: the platform's config
 * surface is generic, but there is exactly one configurable app, and a
 * schema-driven form would be a renderer nobody can read for a shape nobody
 * else has.
 */
export function TenantComplianceCheckConfigCell({
  tenantId,
  app
}: TenantComplianceCheckConfigCellProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const setConfig = useSetTenantAppConfig(tenantId);

  return (
    <>
      <Button variant="outline" size="small" onClick={() => setOpen(true)}>
        {t('adminSettings.tenant.appConfigure')}
      </Button>
      {open && (
        <ComplianceCheckConfigDialog
          stored={app.config}
          isSaving={setConfig.isPending}
          onClose={() => setOpen(false)}
          onSave={(config) =>
            setConfig.mutate({ appId: app.appId, config }, { onSuccess: () => setOpen(false) })
          }
        />
      )}
    </>
  );
}
